import React from "react";
import { Card, CardHeader, CardContent } from "../../../../components/ui/card";
import { getImageUrl } from "../../../../lib/media";
import { PromotionalCode } from "./PromotionalCode";
import { useCheckoutContext } from "../CheckoutContext";
import { CheckoutWizardStepId } from "./checkoutWizard.constants";
import {
  getPackageSummaryDescription,
  getPackageSummaryName,
} from "./packageSummaryCopy";

interface OrderSummaryProps {
  currentStep: CheckoutWizardStepId;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({ currentStep }) => {
  const {
    packageInfo,
    state,
    getFinalPrice,
    formatCurrency,
    formatPercentage,
    formatDiscountFull,
  } = useCheckoutContext();

  if (!packageInfo) {
    return (
      <Card id="order-summary" className="w-full shadow-none lg:sticky lg:top-[calc(4.5rem+2.5rem)]">
        <CardHeader className="space-y-0 px-6 pb-0 pt-6">
          <h2 className="text-lg font-semibold text-foreground">Resumo do pedido</h2>
        </CardHeader>
        <CardContent className="px-6 pb-6 pt-4">
          <p className="text-sm text-text-secondary">Nenhum pacote selecionado</p>
        </CardContent>
      </Card>
    );
  }

  const finalPrice = getFinalPrice();
  const packageName = getPackageSummaryName(packageInfo.name);
  const packageDescription = getPackageSummaryDescription(packageInfo.description);

  return (
    <Card id="order-summary" className="w-full shadow-none lg:sticky lg:top-[calc(4.5rem+2.5rem)]">
      <CardHeader className="space-y-0 px-6 pb-0 pt-6">
        <h2 className="text-lg font-semibold text-foreground">Resumo do pedido</h2>
      </CardHeader>
      <CardContent className="flex flex-col gap-5 px-6 pb-6 pt-4">
        <div>
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1.5">
              <h3 className="text-base font-medium text-foreground">{packageName}</h3>
              {packageDescription ? (
                <p className="text-sm text-text-secondary line-clamp-2">{packageDescription}</p>
              ) : null}
              {packageInfo.modulesCount ? (
                <p className="text-sm text-text-secondary">
                  {packageInfo.modulesCount}{" "}
                  {packageInfo.modulesCount === 1 ? "módulo" : "módulos"}
                </p>
              ) : null}
            </div>
            {packageInfo.imageUrl ? (
              <img
                src={getImageUrl(packageInfo.imageUrl)}
                alt={packageName}
                className="w-20 h-14 sm:w-[120px] sm:h-[80px] rounded-lg object-cover shrink-0"
              />
            ) : null}
          </div>
          <div className="space-y-1.5 text-sm text-text-secondary">
            {packageInfo.modality ? <p>Modalidade: {packageInfo.modality}</p> : null}
            {packageInfo.level ? <p>Nível: {packageInfo.level}</p> : null}
            {packageInfo.duration ? <p>Tempo estimado: {packageInfo.duration}</p> : null}
            {packageInfo.isRecurring ? (
              <p className="text-foreground font-medium">
                Assinatura
                {packageInfo.recurrenceIntervalMonths === 1
                  ? " mensal"
                  : packageInfo.recurrenceIntervalMonths === 3
                    ? " trimestral"
                    : packageInfo.recurrenceIntervalMonths
                      ? ` a cada ${packageInfo.recurrenceIntervalMonths} meses`
                      : ""}
                {" · renovação automática no cartão"}
              </p>
            ) : null}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Subtotal</span>
            <span>
              {formatCurrency(
                state.orderSubtotal !== null && state.orderSubtotal !== undefined
                  ? state.orderSubtotal
                  : packageInfo.price || 0
              )}
            </span>
          </div>
          {state.orderDiscount !== null &&
            state.orderDiscount !== undefined &&
            state.orderDiscount > 0 && (
              <div className="flex justify-between items-center text-success">
                <span>Desconto aplicado</span>
                <span>{formatCurrency(state.orderDiscount)}</span>
              </div>
            )}
          {!state.orderDiscount &&
            packageInfo.discount !== undefined &&
            packageInfo.discount !== null &&
            packageInfo.discount > 0 && (
              <div className="flex justify-between items-center text-success">
                <span>Desconto do pacote</span>
                <span>{formatDiscountFull(packageInfo.price || 0, packageInfo.discount)}</span>
              </div>
            )}
          {state.promoCode.applied ? (
            <div className="flex justify-between items-center text-success">
              <span>Cupom promocional</span>
              <span>
                {state.promoCode.discountType === "freeShipping"
                  ? "100% de desconto"
                  : state.promoCode.discountType === "fixedAmount" &&
                      state.promoCode.discountValue !== undefined
                    ? `-${formatCurrency(state.promoCode.discountValue)}`
                    : `-${formatPercentage(state.promoCode.discount)}`}
              </span>
            </div>
          ) : null}
        </div>

        <div className="flex justify-between items-baseline">
          <span className="font-medium">Total</span>
          <span className="text-2xl font-semibold tabular-nums text-success-subtle">
            {formatCurrency(finalPrice)}
          </span>
        </div>

        {state.paymentError ? (
          <p className="text-sm text-destructive" role="alert">
            {state.paymentError}
          </p>
        ) : null}

        {currentStep > 0 ? <PromotionalCode variant="compact" /> : null}

        {state.isPackageAlreadyPurchased ? (
          <div className="bg-success-muted border border-success rounded-md p-4">
            <p className="text-success-subtle text-sm font-medium text-center">
              Este pacote já está na sua conta.
            </p>
          </div>
        ) : null}

        {state.isPackageUnavailableForPurchase ? (
          <div className="bg-surface-subtle border border rounded-md p-4">
            <p className="text-surface-foreground text-sm font-medium text-center">
              Este pacote não está disponível para compra no momento.
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
